/**
 * LeagueHome — Per-league home screen (v2 redesign)
 *
 * Layout (top → bottom):
 *   1. Light header — kicker (league · season · week · synced), team name H1,
 *      right-aligned StatStrip (Record / Rank / Avg pts / JAI grade / Streak)
 *   2. Scoreboard hero — flat navy card with big mono scores + win prob
 *   3. Live league ticker — navy rounded bar
 *   4. Priority board — this week's actions, rank 01 dominant (emphasis
 *      block), ranks 02-03 in a flush list
 *   5. Tools — centered tile grid (launch-scope tools) + smaller tile grid
 *      of the rest, same dial-icon system across both
 *
 * No emoji in UI chrome — SVG icons only. JAI never talks about itself in
 * the third person in its own copy.
 * Insight content (injuries, GTD, waivers) folds into the priority board.
 */

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import { useLeagues } from '../context/LeagueContext'
import { useAuth } from '../context/AuthContext'
import AppShell from '../components/AppShell'
import ToolHeader from '../components/ToolHeader'
import RemoveLeagueModal from '../components/RemoveLeagueModal'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Semantic, StatStrip, V2Section, V2List, V2ListRow, V2Emphasis, V2Dial, V2ToolGlyphs, V2Icons, pctTone, RefreshButton, statusDisplay } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'
import OfflineBanner from '../components/OfflineBanner'
import { saveOfflineCache, loadOfflineCache, isNetworkError } from '../services/offlineCache'
import { HIDDEN_TOOL_PATHS } from '../config/hiddenTools'
import { planBypassesLaunchScope } from '../config/planAccess'
import { IS_NATIVE_APP } from '../services/runtime'

// ── Dev-only preview mock ─────────────────────────────────────────────────────

const PREVIEW_LEAGUE = {
  name: 'JAI Preview League',
  season: '2024-25',
  teamCount: 10,
  teamName: 'Preview Team',
  scoringType: 'Points',
  syncStatus: 'success',
  // Extended stats — populated by backend when available
  record: '7-3',
  rank: 2,
  avgPoints: 142,
  jaiGrade: 'A-',
  form: ['W', 'L', 'W', 'W', 'W'], // oldest → newest
  draftCompleted: true,
  draftDate: null,
  strategyLocked: false,
  matchup: {
    week: 11,
    opponent: 'Team Chaos',
    projectedPts: 148.2,
    opponentProjectedPts: 131.7,
    winProbability: 68,
  },
}

const PREVIEW_ROSTER = [
  { id: 1966,    playerId: 1966,    name: 'LeBron James',       position: 'F',  percentOwned: 99, injuryStatus: 'ACTIVE' },
  { id: 3975,    playerId: 3975,    name: 'Stephen Curry',      position: 'PG', percentOwned: 98, injuryStatus: 'ACTIVE' },
  { id: 3112335, playerId: 3112335, name: 'Nikola Jokić',       position: 'C',  percentOwned: 97, injuryStatus: 'ACTIVE' },
  { id: 4065648, playerId: 4065648, name: 'Jayson Tatum',       position: 'SF', percentOwned: 96, injuryStatus: 'GTD'    },
  { id: 3934672, playerId: 3934672, name: 'Tyrese Haliburton',  position: 'PG', percentOwned: 94, injuryStatus: 'OUT'    },
  { id: 2490149, playerId: 2490149, name: 'Anthony Davis',      position: 'PF', percentOwned: 93, injuryStatus: 'ACTIVE' },
]

const PREVIEW_ACTIVITY = [
  { id: 1, text: 'Team Chaos dropped Jordan Poole',            time: '2h ago', kind: 'drop'   },
  { id: 2, text: 'The Brow Beaters claimed C. Anthony',        time: '4h ago', kind: 'waiver' },
  { id: 3, text: 'Trade offer received — Morant for Tatum',    time: '5h ago', kind: 'trade'  },
  { id: 4, text: 'Splash Brothers dropped Klay Thompson',      time: '8h ago', kind: 'drop'   },
  { id: 5, text: 'Waiver wire processes in 14 hours',          time: '',       kind: 'news'   },
  { id: 6, text: 'League scoring updated for Week 11',         time: '1d ago', kind: 'news'   },
  { id: 7, text: 'Trade deadline in 3 days',                   time: '',       kind: 'news'   },
]

const IS_PREVIEW = import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === '1'

// Stronger than the shared V2.mute (rgba(10,31,68,0.55)) — used only for the
// moves list's detail/verdict text, which was too faint to read comfortably.
// Kept local rather than raising V2.mute itself: that token has ~90 other
// call sites across the app and a blanket lift needs its own pass.
const READABLE_MUTE = 'rgba(10,31,68,0.72)'

// ── Priority board — degraded fallback only ────────────────────────────────
// This used to be the whole "This week's moves" engine: client-side,
// injury-status-only, capped at 3, never varying week to week. It's now the
// FALLBACK — used only when GET /leagues/:id/moves fails or comes back
// empty, so the section never goes blank. The real engine lives server-side
// (backend/src/services/movesService.js + leagueController.getMoves),
// reading real signals across all nine tools and ranking them with Claude.

function buildFallbackActions(roster, leagueId) {
  const actions = []

  const outPlayers = roster.filter(p => ['OUT', 'IR'].includes(statusDisplay(p.injuryStatus).key))
  const gtdPlayers = roster.filter(p => ['GTD', 'QUESTIONABLE'].includes(statusDisplay(p.injuryStatus).key))

  // Injured starters — most urgent, one entry per player (capped at 2)
  outPlayers.slice(0, 2).forEach(player => {
    actions.push({
      title:   `Find a replacement for ${player.name.split(' ').pop()}`,
      detail:  `${player.name} is ${player.injuryStatus === 'IR' ? 'on IR' : 'out'} this week. Check the wire for a ${player.position} option.`,
      verdict: 'Do this first',
      tool:    'Waiver Wire',
      to:      `/league/${leagueId}/waiver`,
    })
  })

  // GTD watch
  if (gtdPlayers.length > 0) {
    const names = gtdPlayers.slice(0, 2).map(p => p.name.split(' ').pop()).join(' & ')
    actions.push({
      title:   `Confirm ${names} before lineups lock`,
      detail:  `${gtdPlayers.length > 1 ? 'Both are' : `${gtdPlayers[0].name} is`} questionable. Status usually clears a few hours before game time.`,
      verdict: 'Worth a look',
      tool:    'Player Analyser',
      to:      `/league/${leagueId}/player-analyser`,
    })
  }

  // Waiver nudge — always useful as a filler action
  if (actions.length < 3) {
    actions.push({
      title:   'Review this week\'s waiver targets',
      detail:  'A few pickups are lined up based on what your roster needs.',
      verdict: 'Worth a look',
      tool:    'Waiver Wire',
      to:      `/league/${leagueId}/waiver`,
    })
  }

  // Healthy roster — positive filler when nothing else qualifies
  if (outPlayers.length === 0 && gtdPlayers.length === 0 && actions.length < 3) {
    actions.push({
      title:   'Check where you beat the league',
      detail:  'Full roster is active. Nothing to replace this week.',
      verdict: 'Nothing urgent',
      tool:    'Team Analyser',
      to:      `/league/${leagueId}/team-analyser`,
    })
  }

  return actions.slice(0, 3).map((a, i) => ({ ...a, rank: i + 1 }))
}

// ── Form / streak helpers ─────────────────────────────────────────────────────

function computeStreak(form) {
  if (!form || form.length === 0) return null
  const last = form[form.length - 1]
  let count = 0
  for (let i = form.length - 1; i >= 0; i--) {
    if (form[i] === last) count++
    else break
  }
  return { type: last, count }
}

// ── Time helper ───────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000)
  if (seconds < 3600)  return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

// Not every platform gives a draft date (Sleeper, before scheduling) — null
// in, null out, so the card renders its "not yet scheduled" copy instead.
function formatDraftCountdown(dateStr) {
  if (!dateStr) return null
  const target = new Date(dateStr)
  if (Number.isNaN(target.getTime())) return null

  const dateLabel = target.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })

  const diffMs = target.getTime() - Date.now()
  if (diffMs <= 0) return { dateLabel, relative: 'In progress or just started' }

  const days = Math.floor(diffMs / 86400000)
  const hours = Math.floor((diffMs % 86400000) / 3600000)
  const minutes = Math.floor((diffMs % 3600000) / 60000)
  const relative = days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`

  return { dateLabel, relative }
}

// ── Tool tiles — [glyph key into V2ToolGlyphs, name, route slug] ──────────────
// Slug only, not the full path — TOOL_CARDS/TOOL_PILLS live at module scope
// so the per-league id gets spliced in at render time (`/league/${id}/${slug}`).

const TOOL_CARDS = [
  ['draft',     'Draft Room',       'draft'],
  ['waiver',    'Waiver Wire',      'waiver'],
  ['trade',     'Trade Analyser',   'trade'],
  ['scout',     'Opponent Scout',   'opponent-scout'],
  ['player',    'Player Analyser',  'player-analyser'],
  ['team',      'Team Analyser',    'team-analyser'],
  ['insurance', 'Injury Insurance', 'injury-insurance'],
  ['schedule',  'Schedule Surgeon', 'schedule-surgeon'],
  ['notes',     'My Notes',         'notes'],
]

const TOOL_PILLS = [
  ['live',    'Game Night',        'game-night'],
  ['news',    'Hardwood Herald',   'newspaper'],
  ['trophy',  'Championship Odds', 'championship-odds'],
  ['bench',   'Bench Regret',      'bench-regret'],
  ['dna',     'Fantasy DNA',       'fantasy-dna'],
  ['grudge',  'Grudge Match',      'grudge-match'],
  ['power',   'Power Rankings',    'power-rankings'],
  ['trash',   'Trash Talk',        'trash-talk'],
  ['crystal', 'Crystal Ball',      'crystal-ball'],
  ['player',  'Team News',         'team-news'],
  ['globe',   'League News',       'league-news'],
]

// ── Main component ────────────────────────────────────────────────────────────

export default function LeagueHome() {
  const { id } = useParams()
  const { setActiveLeagueId, removeLeague } = useLeagues()
  const { refreshPlan, plan } = useAuth()
  const navigate = useNavigate()

  const [league,   setLeague]   = useState(IS_PREVIEW ? PREVIEW_LEAGUE   : null)
  const [roster,   setRoster]   = useState(IS_PREVIEW ? PREVIEW_ROSTER   : [])
  const [activity, setActivity] = useState(IS_PREVIEW ? PREVIEW_ACTIVITY : [])
  const [moves,    setMoves]    = useState([])
  const [showAllMoves, setShowAllMoves] = useState(false)
  const [loading,  setLoading]  = useState(!IS_PREVIEW)
  const [error,    setError]    = useState('')
  const [showRemove, setShowRemove] = useState(false)
  const [syncing,  setSyncing]  = useState(false)
  const [syncNote, setSyncNote] = useState('')
  // Non-null once a load fell back to the cached snapshot from an earlier
  // successful visit — see services/offlineCache.js. { cachedAt } drives
  // OfflineBanner's "from <time>" text.
  const [offlineInfo, setOfflineInfo] = useState(null)

  // Removing frees a slot under the plan's league limit, so refresh the plan
  // state before leaving — otherwise "Add League" can still look blocked.
  const handleRemoved = async () => {
    removeLeague(Number(id))
    setShowRemove(false)
    await refreshPlan()
    navigate('/chat', { replace: true })
  }

  useEffect(() => {
    if (!IS_PREVIEW) loadData()
    setActiveLeagueId(Number(id))
  }, [id])

  // silent: true skips the full-page loading spinner — used after a manual
  // sync, where the page is already showing and should just update in place
  // rather than flash back to the loading screen.
  const loadData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    setError('')
    try {
      const [leagueRes, rosterRes, activityRes, movesRes, matchupRes] = await Promise.allSettled([
        leaguesApi.get(id),
        leaguesApi.getRoster(id),
        leaguesApi.getTicker(id),
        leaguesApi.getMoves(id),
        leaguesApi.getMatchup(id),
      ])

      // Offline fallback: only when EVERY request failed AND at least one
      // failure looks like a real network drop (no response ever came back
      // — see isNetworkError), not when the server answered with a 4xx/5xx.
      // A reachable server giving an honest error should never be masked by
      // stale cached data.
      const allFailed = [leagueRes, rosterRes, activityRes, movesRes, matchupRes].every(r => r.status === 'rejected')
      const looksOffline = allFailed && [leagueRes, rosterRes, activityRes, movesRes, matchupRes].some(r => isNetworkError(r.reason))
      if (looksOffline) {
        const cached = await loadOfflineCache('leagueHome', id)
        if (cached) {
          setLeague(cached.data.league)
          setRoster(cached.data.roster)
          setActivity(cached.data.activity)
          setMoves(cached.data.moves)
          setOfflineInfo({ cachedAt: cached.cachedAt })
          return
        }
      }

      // Got at least the primary payload — a real (if partial) success, so
      // any earlier offline state no longer applies.
      if (leagueRes.status === 'fulfilled') setOfflineInfo(null)

      let cacheLeague = null, cacheRoster = null, cacheActivity = null, cacheMoves = null

      if (leagueRes.status === 'fulfilled') {
        cacheLeague = leagueRes.value.data.league
        setLeague(cacheLeague)
      }
      if (rosterRes.status === 'fulfilled') {
        cacheRoster = rosterRes.value.data.players || []
        setRoster(cacheRoster)
      }
      // Empty on failure or an empty result — buildFallbackActions covers
      // both cases the same way, so there's nothing else to branch on here.
      if (movesRes.status === 'fulfilled') {
        cacheMoves = movesRes.value.data?.moves || []
        setMoves(cacheMoves)
      }
      // Real record/rank/avg points/form. `matchup`/`jaiGrade` come back
      // null until the projection model exists — the render below already
      // hides those cells when absent, so there's nothing else to branch on.
      if (matchupRes.status === 'fulfilled') {
        cacheLeague = { ...(cacheLeague || {}), ...matchupRes.value.data }
        setLeague(prev => ({ ...prev, ...matchupRes.value.data }))
      }

      if (activityRes.status === 'fulfilled') {
        // /ticker responds { items: [{ id, text, at, kind }] } — real league
        // transactions (kind: add/drop/trade/waiver) first, sport news
        // (kind: 'news') filling the rest. kind drives the ticker's source
        // marker so sport-wide news is never shown as if it were this
        // league's own activity.
        const raw = activityRes.value.data?.items || []
        const mapped = raw
          .map((item, i) => ({
            id:   item.id ?? i,
            text: item.text || '',
            time: item.at ? timeAgo(item.at) : '',
            kind: item.kind,
          }))
          .filter(item => item.text.length > 0)
        if (mapped.length > 0) {
          cacheActivity = mapped
          setActivity(mapped)
        }
      }

      // Cache whatever came back, as long as the primary league payload
      // succeeded — a snapshot missing the league name/record isn't worth
      // falling back to later. Partial (roster/activity/moves failed this
      // time but succeeded on an earlier visit) is fine — a slightly stale
      // roster next to a fresh league record beats an all-or-nothing cache.
      if (cacheLeague) {
        saveOfflineCache('leagueHome', id, {
          league: cacheLeague, roster: cacheRoster || roster, activity: cacheActivity || activity, moves: cacheMoves || moves,
        })
      }
    } catch {
      setError('Failed to load league data')
    } finally {
      if (!silent) setLoading(false)
    }
  }

  // Re-pulls team name, record, and roster from the platform. The stored
  // teamName only ever gets recomputed when a sync actually runs — nothing
  // does that automatically after the first connection — so this is the only
  // way to pick up a corrected value (e.g. after a platform-side data fix)
  // without disconnecting and reconnecting the whole league.
  const handleSync = async () => {
    setSyncing(true)
    setSyncNote('')
    try {
      await leaguesApi.sync(id)
      await loadData({ silent: true })
      setSyncNote('Synced just now.')
    } catch (err) {
      setSyncNote(err.response?.data?.error || 'Sync failed — try again in a moment.')
    } finally {
      setSyncing(false)
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <div className="text-sm" style={{ color: V2.faint }}>Loading league…</div>
        </div>
      </AppShell>
    )
  }

  if (error || !league) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-full min-h-screen">
          <div className="text-center">
            <p className="mb-3 text-sm" style={{ color: V2.error }}>{error || 'League not found'}</p>
            <button onClick={loadData} className="text-sm hover:underline" style={{ color: V2.blue }}>
              Try again
            </button>
          </div>
        </div>
      </AppShell>
    )
  }

  // Derived values — computed once per render from available data
  const streak   = computeStreak(league.form)
  // Real engine first; the old client-side placeholder only steps in when
  // the backend call failed or genuinely found nothing to say.
  const actions  = moves.length > 0 ? moves : buildFallbackActions(roster, id)
  const matchup  = league.matchup

  // Header StatStrip — only cells with data
  const statCells = []
  if (league.record)    statCells.push([league.record, 'Record'])
  if (league.rank)      statCells.push([`#${league.rank}`, 'Rank'])
  if (league.avgPoints) statCells.push([String(league.avgPoints), 'Avg pts'])
  if (league.jaiGrade)  statCells.push([league.jaiGrade, 'JAI grade', true])
  if (streak && streak.count >= 2) {
    statCells.push([`${streak.type}${streak.count}`, 'Streak', streak.type === 'W' ? V2Semantic.hot : V2Semantic.cold])
  }

  // Kicker: {LEAGUE} · {SEASON} · WEEK {N} · SYNCED
  const kickerParts = [league.name]
  if (league.season)  kickerParts.push(league.season)
  if (matchup?.week)  kickerParts.push(`Week ${matchup.week}`)
  if (league.syncStatus === 'success') kickerParts.push('Synced')
  if (league.syncStatus === 'error')   kickerParts.push('Sync failed')

  // Pre-launch, the extra tools are hidden from the pill row. Plans that
  // bypass launch scope (Elite) are allowed to OPEN them — HiddenRoute lets
  // them through — so show them the pills too, otherwise those tools are
  // unreachable without typing the URL.
  const visiblePills = planBypassesLaunchScope(plan)
    ? TOOL_PILLS
    : TOOL_PILLS.filter(([, , slug]) => !HIDDEN_TOOL_PATHS.includes(slug))

  return (
    <AppShell>
      <style>{`
        @keyframes jai-ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div style={{ padding: IS_NATIVE_APP ? '16px 16px 30px' : '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 920, margin: '0 auto' }}>

          {offlineInfo && <OfflineBanner cachedAt={offlineInfo.cachedAt} />}

          {/* ── 1. Header row — centered, no back control (this is the destination) ── */}
          <ToolHeader
            leagueName={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                {kickerParts.map((part, i) => (
                  <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {i > 0 && <span style={{ opacity: 0.5 }}>·</span>}
                    {part === 'Synced'
                      ? <span style={{ color: V2.success, display: 'flex', alignItems: 'center', gap: 3 }}>{V2Icons.check} Synced</span>
                      : part === 'Sync failed'
                        ? <span style={{ color: V2.error }}>Sync failed</span>
                        : <span>{part}</span>}
                  </span>
                ))}
              </div>
            }
            title={league.teamName || league.name}
          />
          {statCells.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: IS_NATIVE_APP ? 14 : 24, marginTop: -10 }}>
              <StatStrip cells={statCells} />
            </div>
          )}

          {/* ── 2. Scoreboard hero — flat navy card ───────────────────────── */}
          {/* Pre-draft: a countdown card in this slot instead (there's no
              matchup to project yet) — see DraftCountdownCard below. Once
              drafted, two variants share this shell: a points league gets two
              big projected-point numbers (unchanged from the original
              design); an H2H-Categories league can't be represented by a
              single number (it doesn't decide a winner that way), so it gets
              a projected category record instead, plus the per-category
              breakdown underneath. See services/projectionService.js.
              The Strategy Establishment CTA only shows pre-draft (below) —
              previously the only route to it was League Home → Draft Board
              → Strategy, so this is still a real improvement even though
              it's gone once drafted. It does NOT belong in the post-draft,
              no-matchup case: each season is its own League row (unique per
              user+league+season — see League.js), so draftCompleted stays
              true for that row for the rest of that league's existence,
              including deep into its off-season. A missing matchup here is
              therefore always a mid-season data gap (bye week, schedule not
              published yet, a fetch hiccup) for THIS season, never "time to
              draft again" — that's a different, fresh League row with its
              own draftCompleted:false. */}
          {!league.draftCompleted && (
            <DraftCountdownCard leagueId={id} draftDate={league.draftDate} strategyLocked={league.strategyLocked} />
          )}
          {league.draftCompleted && !matchup && <NoMatchupStrip preseason={league.matchupReason === 'no-games'} />}
          {matchup && (
            <div style={{ background: V2.navy, borderRadius: 18, padding: IS_NATIVE_APP ? '14px 16px' : '22px 30px', color: 'white', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: IS_NATIVE_APP ? 8 : 16, flexWrap: 'wrap', rowGap: 4 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: IS_NATIVE_APP ? 6 : 8 }}>
                  <span style={{ fontSize: IS_NATIVE_APP ? 9 : 10.5, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.68)', fontFamily: JAI_FONTS.mono }}>
                    Week {matchup.week} matchup
                  </span>
                  {/* "Projected" vs. the ticker's "Live" tag below — same
                      uppercase-mono vocabulary so the two are never confused. */}
                  <span style={{ fontSize: IS_NATIVE_APP ? 8 : 9.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(58,124,255,0.85)', fontFamily: JAI_FONTS.mono, background: 'rgba(58,124,255,0.12)', padding: '2px 7px', borderRadius: 5 }}>
                    Projected
                  </span>
                </span>
                <Link to={`/league/${id}/opponent-scout`} style={{ fontSize: IS_NATIVE_APP ? 10.5 : 11.5, fontWeight: 600, color: V2.blueBright, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                  Scout {matchup.opponent} →
                </Link>
              </div>
              {IS_NATIVE_APP ? (
                // Name-beside-score (web's layout, below) squeezed the name
                // into too little width once the score number sat next to
                // it — "Splash Brothers" truncated to "Splash Bro..." even
                // though it's not a long name. Stacking name-above-score per
                // side gives the name the full column width instead, at the
                // cost of a bit more card height — the trade requested
                // directly. Opponent record/rank isn't shown here: the
                // matchup API doesn't return it for the opponent today
                // (only for your own team), so there's nothing real to put
                // on that line without a backend change.
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{league.teamName || 'Your Team'}</div>
                    <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.68)', fontFamily: JAI_FONTS.mono, marginTop: 1 }}>
                      {[league.record, league.rank ? `RANK ${league.rank}` : null].filter(Boolean).join(' · ')}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: V2.blueBright, fontFamily: JAI_FONTS.mono, letterSpacing: '-0.04em', marginTop: 4 }}>
                      {matchup.categories ? matchup.projectedCategoryRecord.split('-')[0] : matchup.projectedPts.toFixed(1)}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, textAlign: 'center', paddingTop: 2 }}>
                    <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', fontFamily: JAI_FONTS.mono, marginBottom: 3 }}>Win prob</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: pctTone(matchup.winProbability), fontFamily: JAI_FONTS.mono }}>{matchup.winProbability}%</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{matchup.opponent}</div>
                    <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.4)', fontFamily: JAI_FONTS.mono, marginTop: 1 }}>&nbsp;</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: 'rgba(255,255,255,0.78)', fontFamily: JAI_FONTS.mono, letterSpacing: '-0.04em', marginTop: 4 }}>
                      {matchup.categories ? matchup.projectedCategoryRecord.split('-')[1] : matchup.opponentProjectedPts.toFixed(1)}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{league.teamName || 'Your Team'}</div>
                      <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.68)', fontFamily: JAI_FONTS.mono, marginTop: 2 }}>
                        {[league.record, league.rank ? `RANK ${league.rank}` : null].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div style={{ marginLeft: 'auto', fontSize: 42, fontWeight: 800, color: V2.blueBright, fontFamily: JAI_FONTS.mono, letterSpacing: '-0.04em' }}>
                      {matchup.categories ? matchup.projectedCategoryRecord.split('-')[0] : matchup.projectedPts.toFixed(1)}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, textAlign: 'center' }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', fontFamily: JAI_FONTS.mono, marginBottom: 4 }}>Win prob</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: pctTone(matchup.winProbability), fontFamily: JAI_FONTS.mono }}>{matchup.winProbability}%</div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                    <div style={{ fontSize: 42, fontWeight: 800, color: 'rgba(255,255,255,0.78)', fontFamily: JAI_FONTS.mono, letterSpacing: '-0.04em' }}>
                      {matchup.categories ? matchup.projectedCategoryRecord.split('-')[1] : matchup.opponentProjectedPts.toFixed(1)}
                    </div>
                    <div style={{ marginLeft: 'auto', textAlign: 'right', minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{matchup.opponent}</div>
                    </div>
                  </div>
                </div>
              )}
              {matchup.categories && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ textAlign: 'center', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', fontFamily: JAI_FONTS.mono, margin: '4px 0 10px' }}>
                    Projected categories — {matchup.projectedCategoryRecord}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                    {matchup.categories.map((c) => {
                      const color = c.result === 'W' ? V2Semantic.hot : c.result === 'L' ? V2Semantic.cold : 'rgba(255,255,255,0.45)'
                      const fmt = (v) => (c.key === 'FG%' || c.key === 'FT%' ? `.${String(Math.round(v * 1000)).padStart(3, '0')}` : v)
                      return (
                        <div key={c.key} title={`${c.label}: ${fmt(c.mine)} vs ${fmt(c.opponent)}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '5px 8px' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: 'rgba(255,255,255,0.8)', fontFamily: JAI_FONTS.mono }}>{c.key}</span>
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'white', fontFamily: JAI_FONTS.mono }}>{fmt(c.mine)}</span>
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>/</span>
                          <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.78)', fontFamily: JAI_FONTS.mono }}>{fmt(c.opponent)}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {/* The one gradient the app keeps — win probability, read as emphasis not decoration */}
              <div style={{ marginTop: IS_NATIVE_APP ? 10 : 16, background: 'rgba(255,255,255,0.12)', borderRadius: 999, height: 6, overflow: 'hidden' }}>
                <div style={{ width: `${matchup.winProbability}%`, background: V2.winProbGradient, height: '100%' }} />
              </div>
            </div>
          )}

          {/* ── 3. Live league ticker ─────────────────────────────────────── */}
          {activity.length > 0 && <LeagueTicker items={activity} />}

          {/* ── 4. Priority board — rank 01 dominant, 02-05 recede, rest behind a toggle ── */}
          {actions.length > 0 && (() => {
            const rest = actions.slice(1)
            const visibleRest = showAllMoves ? rest : rest.slice(0, 4)
            const hiddenCount = rest.length - visibleRest.length
            return (
              <V2Section heading="This week's moves" style={{ marginBottom: 26 }}>
                <V2Emphasis
                  as={Link}
                  to={actions[0].to}
                  style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: actions.length > 1 ? 8 : 0 }}
                >
                  <span style={{ fontSize: 32, fontWeight: 800, color: 'rgba(255,255,255,0.25)', fontFamily: JAI_FONTS.mono, flexShrink: 0, lineHeight: 1 }}>
                    01
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 16.5, fontWeight: 700, color: 'white', marginBottom: 4 }}>
                      {actions[0].title}
                      {actions[0].carriedOver && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginLeft: 8 }}>· still open</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.62)', lineHeight: 1.5 }}>{actions[0].detail}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, padding: '4px 11px', borderRadius: 999, background: 'rgba(255,255,255,0.14)', color: 'white', marginBottom: 9, whiteSpace: 'nowrap' }}>
                      {actions[0].verdict}
                    </div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: V2.blueBright, whiteSpace: 'nowrap' }}>{actions[0].tool} →</div>
                  </div>
                </V2Emphasis>

                {rest.length > 0 && (
                  <V2List>
                    {visibleRest.map(action => (
                      <V2ListRow as={Link} to={action.to} key={action.rank}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: V2.faint, fontFamily: JAI_FONTS.mono, flexShrink: 0, width: 20 }}>
                          {String(action.rank).padStart(2, '0')}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: V2.navy, marginBottom: 2 }}>
                            {action.title}
                            {action.carriedOver && (
                              <span style={{ fontSize: 11, fontWeight: 600, color: V2.faint, marginLeft: 6 }}>· still open</span>
                            )}
                          </div>
                          <div style={{ fontSize: 12.5, color: READABLE_MUTE, lineHeight: 1.45 }}>{action.detail}</div>
                        </div>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: READABLE_MUTE, flexShrink: 0 }}>{action.verdict}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: V2.blue, flexShrink: 0 }}>{action.tool} →</span>
                      </V2ListRow>
                    ))}
                  </V2List>
                )}

                {hiddenCount > 0 && (
                  <button
                    onClick={() => setShowAllMoves(true)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'center', marginTop: 10,
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 12.5, fontWeight: 600, color: V2.blue, fontFamily: JAI_FONTS.tight,
                    }}
                  >
                    Show {hiddenCount} more
                  </button>
                )}
              </V2Section>
            )
          })()}

          {/* ── 5. Tools — centered tile board, one icon system throughout ─── */}
          <V2Section heading="Tools" style={{ marginBottom: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: visiblePills.length > 0 ? 12 : 0 }}>
              {TOOL_CARDS.map(([glyph, title, slug]) => (
                <Link
                  key={title}
                  to={`/league/${id}/${slug}`}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 12, textAlign: 'center', minHeight: 132,
                    background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 14,
                    padding: '22px 10px', textDecoration: 'none', transition: 'border-color 180ms',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(10,31,68,0.18)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = V2.hairSoft}
                >
                  <V2Dial size={44}>{V2ToolGlyphs[glyph]}</V2Dial>
                  <div style={{ fontSize: 14, fontWeight: 700, color: V2.navy }}>{title}</div>
                </Link>
              ))}
            </div>

            {visiblePills.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))', gap: 8 }}>
                {visiblePills.map(([glyph, title, slug]) => (
                  <Link
                    key={title}
                    to={`/league/${id}/${slug}`}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      gap: 6, textAlign: 'center',
                      background: 'white', border: `1px solid ${V2.hairSoft}`, borderRadius: 12,
                      padding: '13px 6px', textDecoration: 'none', transition: 'border-color 180ms',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(58,124,255,0.3)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = V2.hairSoft}
                  >
                    <V2Dial size={30}>{V2ToolGlyphs[glyph]}</V2Dial>
                    <div style={{ fontSize: 10.5, fontWeight: 600, color: 'rgba(10,31,68,0.72)', lineHeight: 1.25 }}>{title}</div>
                  </Link>
                ))}
              </div>
            )}
          </V2Section>

          {/* ── 6. Manage league ──────────────────────────────────────────── */}
          <div style={{ marginTop: 30, paddingTop: 18, borderTop: `1px solid ${V2.hair}` }}>

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 14, flexWrap: 'wrap', paddingBottom: 18, borderBottom: `1px solid ${V2.hair}`,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: V2.navy, letterSpacing: '0.03em', textTransform: 'uppercase' }}>Sync league data</div>
                <div style={{ fontSize: 12, color: V2.mute, marginTop: 2 }}>
                  {syncNote || (league.lastSyncedAt ? `Last synced ${timeAgo(league.lastSyncedAt)}.` : 'Not synced yet.')}
                  {' '}Pulls your latest team name, record, and roster from {(league.platform || 'fantasy').toUpperCase()}.
                </div>
              </div>
              <RefreshButton onClick={handleSync} loading={syncing} label="Sync now" loadingLabel="Syncing…" />
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 14, flexWrap: 'wrap', paddingTop: 18,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: V2.navy, letterSpacing: '0.03em', textTransform: 'uppercase' }}>Delete this league</div>
                <div style={{ fontSize: 12, color: V2.mute, marginTop: 2 }}>
                  Disconnects this league. Your {(league.platform || 'fantasy').toUpperCase()} account is untouched, and you can reconnect any time.
                </div>
              </div>
              <button
                onClick={() => setShowRemove(true)}
                style={{
                  background: 'white', border: '1px solid rgba(214,58,58,0.3)',
                  borderRadius: 999, padding: '8px 16px',
                  fontSize: 12.5, fontWeight: 600, color: V2.error,
                  cursor: 'pointer', transition: 'background 180ms, border-color 180ms',
                  fontFamily: JAI_FONTS.tight, flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(214,58,58,0.06)'; e.currentTarget.style.borderColor = 'rgba(214,58,58,0.5)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = 'rgba(214,58,58,0.3)' }}
              >
                Delete league
              </button>
            </div>

          </div>

        </div>
      </div>

      {showRemove && (
        <RemoveLeagueModal
          league={{ id, name: league.name, platform: league.platform }}
          onClose={() => setShowRemove(false)}
          onRemoved={handleRemoved}
        />
      )}
    </AppShell>
  )
}

// ── Draft countdown — pre-draft stand-in for the scoreboard hero ──────────────
// Shares the hero's navy-card shell so the swap doesn't jar visually once the
// draft completes and the real matchup card takes this slot over.

function DraftCountdownCard({ leagueId, draftDate, strategyLocked }) {
  const countdown = formatDraftCountdown(draftDate)
  const strategyLabel = strategyLocked ? 'Review your strategy' : 'Set your draft strategy'

  return (
    <div style={{ background: V2.navy, borderRadius: 18, padding: '22px 30px', color: 'white', marginBottom: 12 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', fontFamily: JAI_FONTS.mono, marginBottom: 14 }}>
        {countdown ? 'Draft countdown' : 'Draft not yet scheduled'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
        {countdown ? (
          <div>
            <div style={{ fontSize: 30, fontWeight: 800, fontFamily: JAI_FONTS.mono, letterSpacing: '-0.03em' }}>{countdown.relative}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>{countdown.dateLabel}</div>
          </div>
        ) : (
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', maxWidth: 380, lineHeight: 1.5 }}>
            Your league hasn't scheduled a draft date yet — check back once it's set, or sync now if it just was.
          </div>
        )}
        <Link
          to={`/league/${leagueId}/draft/strategy`}
          style={{
            fontSize: 13, fontWeight: 700, color: 'white', background: V2.blue,
            borderRadius: 999, padding: '10px 18px', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          {strategyLabel} →
        </Link>
      </div>
    </div>
  )
}

// ── No-matchup strip — post-draft fallback when there's no matchup to show
// (bye week, platform hasn't published this week's schedule, a fetch
// hiccup). Deliberately makes no claim beyond that — no strategy CTA here,
// since draftCompleted staying true for this League row's whole lifetime
// means this state is never "time to draft again" (see the comment above
// where this is rendered).

function NoMatchupStrip({ preseason = false }) {
  return (
    <div
      style={{
        background: V2.navy, borderRadius: 14, padding: '14px 20px', marginBottom: 12,
        color: 'rgba(255,255,255,0.75)', fontSize: 13, textAlign: 'center',
      }}
    >
      {preseason ? "The season hasn't started yet. Your first matchup shows here once games begin." : 'No matchup to show this week'}
    </div>
  )
}

// ── Live league ticker — navy rounded bar with LIVE cell ──────────────────────
// Honesty rule: a ticker item is only ever labelled as this league's own
// activity if it actually is. Real transactions (add/drop/trade/waiver) get a
// coloured dot; sport-wide news (kind: 'news', or no kind at all — e.g.
// preview data) gets none, so nothing reads as "your league" unless it is.

const TICKER_DOT_COLOR = {
  add:    V2.successOnDark,
  waiver: V2.successOnDark,
  trade:  V2.blueBright,
  drop:   'rgba(255,255,255,0.4)',
}

function LeagueTicker({ items }) {
  const doubled  = [...items, ...items]
  const duration = Math.max(items.length * 5, 20)

  return (
    <div style={{
      background: V2.navyInk, borderRadius: 12, height: 36,
      display: 'flex', alignItems: 'center', overflow: 'hidden', marginBottom: 24,
    }}>
      {/* Pinned "LIVE" label cell */}
      <div style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
        padding: '0 14px', borderRight: '1px solid rgba(58,124,255,0.2)',
        height: '100%',
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: V2.successOnDark, boxShadow: '0 0 8px rgba(74,222,148,0.8)', flexShrink: 0 }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: V2.successOnDark, letterSpacing: '0.14em', textTransform: 'uppercase', fontFamily: JAI_FONTS.mono }}>
          Live
        </span>
      </div>

      {/* Scrolling items */}
      <div style={{ overflow: 'hidden', flex: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
        <div style={{
          display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
          animation: `jai-ticker ${duration}s linear infinite`,
          willChange: 'transform',
        }}>
          {doubled.map((item, i) => (
            <span
              key={i}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'rgba(255,255,255,0.82)', padding: '0 20px', borderRight: '1px solid rgba(255,255,255,0.06)', fontFamily: JAI_FONTS.tight }}
            >
              {TICKER_DOT_COLOR[item.kind] && (
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: TICKER_DOT_COLOR[item.kind], flexShrink: 0 }} />
              )}
              {item.text}
              {item.time && <span style={{ color: 'rgba(255,255,255,0.5)', marginLeft: 7, fontSize: 11.5 }}>{item.time}</span>}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
